import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ArrowRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Universal interest / waitlist / notify-me modal. Every programme
 * card with a non-live status opens this. Submissions land in the
 * `programme_interest` MongoDB collection and surface in the admin
 * dashboard alongside newsletter + WhatsApp counts.
 */
export default function RegisterInterestModal({
  open,
  onClose,
  programme,
  status,
  cta = "Register Interest",
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [audience, setAudience] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/programme-interest`, {
        programme,
        status,
        email,
        name: name || undefined,
        audience: audience || undefined,
        page_source: typeof window !== "undefined" ? window.location.pathname : null,
        device_source: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setDone(true);
      toast.success("You're on the list — we'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Couldn't submit — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="register-interest-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-zinc-950 border border-amber-400/30 p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="register-interest-close"
          className="absolute top-3 right-3 text-zinc-500 hover:text-amber-400 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {done ? (
          <div className="text-center py-4">
            <div className="mx-auto h-12 w-12 inline-flex items-center justify-center bg-amber-400 text-black">
              <Check className="h-6 w-6" />
            </div>
            <h3 className="mt-5 font-display text-2xl font-bold uppercase">
              You're in.
            </h3>
            <p className="mt-3 text-sm text-zinc-300">
              We'll send the first update on <span className="text-amber-300">{programme}</span> straight to your inbox.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-2 border border-amber-400/40 hover:border-amber-400 text-amber-300 px-5 py-2.5 text-xs uppercase tracking-[0.25em] font-bold transition-colors"
            >
              Continue exploring
            </button>
          </div>
        ) : (
          <>
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
              {cta}
            </p>
            <h3 className="mt-2 font-display text-2xl sm:text-3xl font-bold uppercase leading-tight">
              {programme}
            </h3>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
              Drop your details — we'll reach out the moment this one moves.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                data-testid="register-interest-name"
                className="w-full bg-black border border-white/15 focus:border-amber-400 outline-none px-4 py-3 text-sm text-white placeholder:text-zinc-500"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="register-interest-email"
                className="w-full bg-black border border-white/15 focus:border-amber-400 outline-none px-4 py-3 text-sm text-white placeholder:text-zinc-500"
              />
              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                data-testid="register-interest-audience"
                className="w-full bg-black border border-white/15 focus:border-amber-400 outline-none px-4 py-3 text-sm text-white"
              >
                <option value="">I'm a… (optional)</option>
                <option value="family">Family / parent</option>
                <option value="school">School / educator</option>
                <option value="young-person">Young person 16–25</option>
                <option value="community">Community member</option>
                <option value="artist">Artist / creative</option>
                <option value="organisation">Organisation / corporate</option>
                <option value="funder">Funder / partner</option>
                <option value="other">Other</option>
              </select>
              <button
                type="submit"
                disabled={loading || !email}
                data-testid="register-interest-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:bg-amber-400/40 text-black px-5 py-3.5 font-bold uppercase tracking-[0.22em] text-[11px] transition-colors"
              >
                {loading ? "Sending…" : cta}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <p className="text-[10px] text-zinc-600 leading-relaxed">
                We'll only contact you about <span className="text-zinc-400">{programme}</span>. No spam. Unsubscribe anytime.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
